/*
  Case studies v2: <cs-atmosphere> chromatic page atmosphere (sections/cs-hero.liquid).
  A fixed, full-viewport WebGL canvas behind the page: slow, blurred folds of brand
  colour (electric blue, icy blue, pale cyan) on white, kept to the edges so the middle
  of the screen stays clear for the headline ("Chromatic Fluid").
  - Scrolling through the hero (u_fade 0 → 1) lets the colour drift outwards and soften;
    below the hero it holds at data-trace strength, so light sections keep a trace of it
    (they are transparent while this is live; dark sections keep their own backgrounds).
  - Scrolling also moves the fluid: the colour field drifts with the page (a slower,
    parallax-like pace) and stirs slightly, eased so it glides rather than tracks rigidly.
  - Rendered at half resolution (the field is soft, so it upscales invisibly) and at a
    lower frame rate once past the hero. Paused while the tab is hidden.
  - Reduced motion: one still frame, redrawn only on scroll/resize.
  - No WebGL: the canvas keeps a static CSS gradient in the same palette (cs-hero.css).
  - Theme editor: disconnectedCallback stops the loop and releases the GL context.
*/
(() => {
  if (customElements.get('cs-atmosphere')) return;

  const VERT = 'attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';
  const FRAG = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_fade;
uniform float u_floor;
uniform float u_scroll;   /* eased scroll position, in viewport heights */

const vec3 WHITE = vec3(1.0);
const vec3 BLUE  = vec3(0.184, 0.482, 0.965);   /* #2F7BF6 electric blue */
const vec3 ICE   = vec3(0.780, 0.871, 1.000);   /* icy blue */
const vec3 CYAN  = vec3(0.820, 0.957, 0.984);   /* pale cyan */

/* 2D simplex noise (Ashima Arts, MIT) */
vec3 mod289(vec3 x){return x-floor(x*(1./289.))*289.;}
vec2 mod289(vec2 x){return x-floor(x*(1./289.))*289.;}
vec3 permute(vec3 x){return mod289(((x*34.)+1.)*x);}
float snoise(vec2 v){
  const vec4 C=vec4(.211324865405187,.366025403784439,-.577350269189626,.024390243902439);
  vec2 i=floor(v+dot(v,C.yy));vec2 x0=v-i+dot(i,C.xx);
  vec2 i1=(x0.x>x0.y)?vec2(1.,0.):vec2(0.,1.);
  vec4 x12=x0.xyxy+C.xxzz;x12.xy-=i1;i=mod289(i);
  vec3 p=permute(permute(i.y+vec3(0.,i1.y,1.))+i.x+vec3(0.,i1.x,1.));
  vec3 m=max(.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.);m=m*m;m=m*m;
  vec3 x=2.*fract(p*C.www)-1.;vec3 h=abs(x)-.5;vec3 ox=floor(x+.5);vec3 a0=x-ox;
  m*=1.79284291400159-.85373472095314*(a0*a0+h*h);
  vec3 g;g.x=a0.x*x0.x+h.x*x0.y;g.yz=a0.yz*x12.xz+h.yz*x12.yw;
  return 130.*dot(m,g);
}
/* Three soft octaves: low detail reads as an optical blur */
float fbm(vec2 p){
  float v=0.,a=.55;mat2 r=mat2(.8,.6,-.6,.8);
  for(int i=0;i<3;i++){v+=a*snoise(p);p=r*p*1.9+vec2(3.1,4.7);a*=.45;}
  return v*.5+.5;
}
float hash(vec2 p){return fract(sin(dot(p,vec2(12.9898,78.233)))*43758.5453);}

/* 0 in the middle of the screen, 1 at every edge. Scrolling widens the clear zone a
   little and softens the edge, so the colour diffuses outwards rather than vanishing. */
float edgeMask(vec2 p){
  float aspect=u_res.x/u_res.y;
  vec2 q=p/vec2(aspect*.5,.5);
  float d=length(q*vec2(.92,1.));
  return smoothstep(mix(.42,.55,u_fade),mix(1.25,1.45,u_fade),d);
}

void main(){
  vec2 uv=gl_FragCoord.xy/u_res;
  vec2 p=(uv-.5)*vec2(u_res.x/u_res.y,1.);
  /* Scroll: the field drifts upwards with the page at a slower pace and the flow
     advances a little, so scrolling visibly stirs the colour. GL's y axis points up,
     so sampling lower (minus) moves the pattern up on screen as the page scrolls down. */
  float t=u_time*.03+u_scroll*.18;
  vec2 s=p*.48+vec2(u_scroll*.04,-u_scroll*.32);
  vec2 q=vec2(fbm(s+vec2(0.,t)),fbm(s+vec2(5.2,1.3)-t*.8));
  vec2 r=vec2(fbm(s+1.3*q+vec2(1.7,9.2)+t*.6),fbm(s+1.3*q+vec2(8.3,2.8)-t*.5));
  float f=fbm(s+1.2*r);
  /* subtle chromatic separation: the blue samples the field slightly apart */
  float fb=fbm(s+1.2*r+vec2(.08,-.05));
  vec3 col=mix(CYAN,ICE,smoothstep(.2,.85,q.x));
  col=mix(col,BLUE,smoothstep(.42,.9,fb)*.88);
  float strength=mix(1.,u_floor,smoothstep(0.,1.,u_fade));
  float amt=min(1.,pow(edgeMask(p),1.35)*1.15)*strength*smoothstep(.12,.8,f+.1*q.y);
  col=mix(WHITE,col,clamp(amt,0.,1.));
  col+=(hash(gl_FragCoord.xy+fract(u_time))-.5)/255.;   /* dither: no banding */
  gl_FragColor=vec4(col,1.);
}`;

  const SCALE = 0.5;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class CsAtmosphere extends HTMLElement {
    connectedCallback() {
      this.canvas = this.querySelector('canvas');
      this.hero = this.closest('.cs-hero') || this.parentElement;
      this.trace = Math.min(1, Math.max(0, (parseFloat(this.dataset.trace) || 30) / 100));
      this.fade = 0;
      this.scrollTarget = window.scrollY / window.innerHeight;
      this.scrollPos = this.scrollTarget;
      this.start = performance.now() - 40000; // start mid-flow rather than from a cold frame
      this.frame = 0;
      // Light .cs sections turn transparent while the atmosphere is live (cs-core.css).
      document.documentElement.classList.add('cs-atmos-live');

      if (!this.canvas || !this.setupGL()) {
        this.classList.add('is-static');
        return;
      }

      this.ac = new AbortController();
      const opts = { signal: this.ac.signal, passive: true };
      window.addEventListener('scroll', () => this.onScroll(), opts);
      window.addEventListener('resize', () => this.onResize(), opts);
      document.addEventListener('visibilitychange', () => this.run(), { signal: this.ac.signal });
      reduceMotion.addEventListener('change', () => this.run(), { signal: this.ac.signal });
      this.canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); this.stop(); this.classList.add('is-static'); }, { signal: this.ac.signal });

      this.resize();
      this.onScroll();
      this.run();
    }

    disconnectedCallback() {
      if (this.ac) this.ac.abort();
      cancelAnimationFrame(this.resizeRaf);
      this.stop();
      const lose = this.gl && this.gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
      this.gl = null;
      document.documentElement.classList.remove('cs-atmos-live');
    }

    setupGL() {
      const gl = this.canvas.getContext('webgl', { antialias: false, alpha: false, depth: false, powerPreference: 'low-power' });
      if (!gl) return false;
      const compile = (type, src) => {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
      };
      const vs = compile(gl.VERTEX_SHADER, VERT);
      const fs = compile(gl.FRAGMENT_SHADER, FRAG);
      if (!vs || !fs) return false;
      const prog = gl.createProgram();
      gl.attachShader(prog, vs);
      gl.attachShader(prog, fs);
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return false;
      gl.useProgram(prog);
      gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
      const loc = gl.getAttribLocation(prog, 'a');
      gl.enableVertexAttribArray(loc);
      gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
      this.u = {
        res: gl.getUniformLocation(prog, 'u_res'),
        time: gl.getUniformLocation(prog, 'u_time'),
        fade: gl.getUniformLocation(prog, 'u_fade'),
        floor: gl.getUniformLocation(prog, 'u_floor'),
        scroll: gl.getUniformLocation(prog, 'u_scroll')
      };
      this.gl = gl;
      this.classList.add('is-live');
      return true;
    }

    onResize() {
      // Coalesce resize events without restarting the animation. Resizing clears
      // the buffer, so redraw in the same frame even when the idle loop skips it.
      if (this.resizeRaf) return;
      this.resizeRaf = requestAnimationFrame((now) => {
        this.resizeRaf = 0;
        if (!this.gl) return;
        this.resize();
        this.onScroll();
        this.draw(reduceMotion.matches ? this.still() : now);
      });
    }

    resize() {
      const w = Math.max(1, Math.round(window.innerWidth * SCALE));
      const h = Math.max(1, Math.round(window.innerHeight * SCALE));
      if (this.canvas.width !== w || this.canvas.height !== h) {
        this.canvas.width = w;
        this.canvas.height = h;
      }
    }

    onScroll() {
      const h = this.hero ? this.hero.offsetHeight : window.innerHeight;
      const top = this.hero ? this.hero.getBoundingClientRect().top + window.scrollY : 0;
      this.fade = Math.min(1, Math.max(0, (window.scrollY - top) / (h * 0.85)));
      this.scrollTarget = window.scrollY / window.innerHeight;
      if (!this.raf && this.gl) this.draw(this.still());
    }

    // Ease towards the real scroll position; true while still catching up.
    easeScroll() {
      const d = this.scrollTarget - this.scrollPos;
      if (Math.abs(d) < 0.0005) {
        this.scrollPos = this.scrollTarget;
        return false;
      }
      this.scrollPos += d * 0.08;
      return true;
    }

    still() {
      return this.start + 40000;
    }

    draw(now) {
      const gl = this.gl;
      if (!gl) return;
      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      gl.uniform2f(this.u.res, this.canvas.width, this.canvas.height);
      gl.uniform1f(this.u.time, (now - this.start) / 1000);
      gl.uniform1f(this.u.fade, this.fade);
      gl.uniform1f(this.u.floor, this.trace);
      // Reduced motion: no scroll-linked movement.
      gl.uniform1f(this.u.scroll, reduceMotion.matches ? 0 : this.scrollPos);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    stop() {
      cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    run() {
      this.stop();
      if (!this.gl || document.hidden) return;
      if (reduceMotion.matches) {
        this.draw(this.still());
        return;
      }
      const loop = (now) => {
        // Full frame rate over the hero or while the scroll drift is easing;
        // otherwise every third frame once only the trace is left.
        const moving = this.easeScroll();
        if (this.fade < 1 || moving || this.frame++ % 3 === 0) this.draw(now);
        this.raf = requestAnimationFrame(loop);
      };
      this.raf = requestAnimationFrame(loop);
    }
  }

  customElements.define('cs-atmosphere', CsAtmosphere);
})();
